/**
 * ImagingResultsForm Component
 * 
 * Structured entry for imaging and diagnostic study results.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export interface ImagingResult {
  id: string;
  type: ImagingType;
  date: string;
  findings: string;
  bodyPart?: string;
}

export type ImagingType = 
  | 'MRI'
  | 'CT'
  | 'X-Ray'
  | 'EMG/NCS'
  | 'Ultrasound'
  | 'Other';

interface ImagingResultsFormProps {
  value: ImagingResult[];
  onChange: (results: ImagingResult[]) => void;
  disabled?: boolean;
  procedureCategory?: 'Spine' | 'Nerve' | 'Arthroscopy' | 'General';
}

const IMAGING_TYPE_INFO: Record<ImagingType, { label: string; suggestedFor: string[] }> = {
  MRI: {
    label: 'MRI',
    suggestedFor: ['Spine', 'Arthroscopy', 'General'],
  },
  CT: {
    label: 'CT Scan',
    suggestedFor: ['Spine', 'General'],
  },
  'X-Ray': {
    label: 'X-Ray',
    suggestedFor: ['Spine', 'Arthroscopy', 'General'],
  },
  'EMG/NCS': {
    label: 'EMG / Nerve Conduction Study',
    suggestedFor: ['Nerve'],
  },
  Ultrasound: {
    label: 'Ultrasound',
    suggestedFor: ['General'],
  },
  Other: {
    label: 'Other Diagnostic',
    suggestedFor: ['Spine', 'Nerve', 'Arthroscopy', 'General'],
  },
};

const FINDING_SUGGESTIONS: Record<string, string[]> = {
  Spine: [
    'Disc herniation',
    'Spinal stenosis',
    'Spondylolisthesis',
    'Foraminal narrowing',
    'Nerve root compression',
    'Facet arthropathy',
    'Degenerative disc disease',
    'Compression fracture',
  ],
  Nerve: [
    'Positive Tinel sign',
    'Decreased nerve conduction velocity',
    'Denervation potentials',
    'Carpal tunnel syndrome',
    'Cubital tunnel syndrome',
    'Ulnar nerve subluxation',
    'Axonal loss',
  ],
  Arthroscopy: [
    'Meniscal tear',
    'Labral tear',
    'Rotator cuff tear',
    'Cartilage damage',
    'Loose bodies',
    'Ligament injury',
  ],
  General: [
    'Significant pathology',
    'Correlates with symptoms',
    'Progressive disease',
  ],
};

export const ImagingResultsForm: React.FC<ImagingResultsFormProps> = ({
  value,
  onChange,
  disabled = false,
  procedureCategory = 'General',
}) => {
  const [newType, setNewType] = useState<ImagingType>('MRI');

  const handleAdd = () => {
    const newResult: ImagingResult = {
      id: `img-${Date.now()}`,
      type: newType,
      date: '',
      findings: '',
    };
    onChange([...value, newResult]);
  };

  const handleUpdate = (id: string, field: keyof ImagingResult, fieldValue: string) => {
    onChange(
      value.map(r => (r.id === id ? { ...r, [field]: fieldValue } : r))
    );
  };

  const handleRemove = (id: string) => {
    onChange(value.filter(r => r.id !== id));
  };

  const handleAddFindingSuggestion = (id: string, suggestion: string) => {
    const existing = value.find(r => r.id === id);
    if (!existing) return;
    
    const currentFindings = existing.findings;
    const newFindings = currentFindings
      ? `${currentFindings}; ${suggestion}`
      : suggestion;
    
    handleUpdate(id, 'findings', newFindings);
  };

  // Get suggested imaging types for current procedure category
  const suggestedTypes = Object.entries(IMAGING_TYPE_INFO)
    .filter(([_, info]) => info.suggestedFor.includes(procedureCategory))
    .map(([type]) => type as ImagingType);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Imaging / Diagnostic Studies
        </Typography>
        <Tooltip title="Document imaging and diagnostic results that support the diagnosis. Include dates and clinically relevant findings that correlate with symptoms.">
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Stack>

      {/* Suggested imaging types for this procedure */}
      {value.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Recommended for {procedureCategory} procedures:
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {suggestedTypes.map(type => (
              <Chip
                key={type}
                label={IMAGING_TYPE_INFO[type].label}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Existing entries */}
      <Stack spacing={2}>
        {value.map((result) => (
          <Paper
            key={result.id}
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={result.type}
                      label="Type"
                      onChange={(e) => handleUpdate(result.id, 'type', e.target.value)}
                      disabled={disabled}
                    >
                      {Object.entries(IMAGING_TYPE_INFO).map(([type, info]) => (
                        <MenuItem key={type} value={type}>
                          {info.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="date"
                    label="Date"
                    value={result.date}
                    onChange={(e) => handleUpdate(result.id, 'date', e.target.value)}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    size="small"
                    label="Body Part"
                    placeholder="e.g., L-Spine, R Wrist"
                    value={result.bodyPart ?? ''}
                    onChange={(e) => handleUpdate(result.id, 'bodyPart', e.target.value)}
                    disabled={disabled}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  label="Findings"
                  placeholder="Enter clinically relevant findings..."
                  value={result.findings}
                  onChange={(e) => handleUpdate(result.id, 'findings', e.target.value)}
                  disabled={disabled}
                  sx={{ mb: 1 }}
                />
                {FINDING_SUGGESTIONS[procedureCategory] && (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                      Quick add:
                    </Typography>
                    {FINDING_SUGGESTIONS[procedureCategory].slice(0, 5).map((suggestion) => (
                      <Chip
                        key={suggestion}
                        label={suggestion}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddFindingSuggestion(result.id, suggestion)}
                        disabled={disabled}
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemove(result.id)}
                disabled={disabled}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Add new entry */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Imaging Type</InputLabel>
          <Select
            value={newType}
            label="Imaging Type"
            onChange={(e) => setNewType(e.target.value as ImagingType)}
            disabled={disabled}
          >
            {Object.entries(IMAGING_TYPE_INFO).map(([type, info]) => (
              <MenuItem key={type} value={type}>
                {info.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={disabled}
          size="small"
        >
          Add Study
        </Button>
      </Stack>

      {value.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
          No imaging studies added. Diagnostic evidence strengthens the appeal.
        </Typography>
      )}
    </Box>
  );
};
